// Email + password sign-in, for app-review and compliance accounts only.
//
// Everyone who signs up here uses the one-time email link on /signin — there is
// no password to phish, reuse or leak, and that is the flow the product ships.
// This page exists for the one case that flow cannot serve: Meta and Google
// reviewers, and auditors, who need a login they can repeat on demand from a
// shared mailbox they may not be able to read in time.
//
// It is deliberately unlisted. Nothing links to it, it is not in the sitemap,
// and robots.txt keeps it out of the index — the URL is handed to a reviewer in
// the submission form and nowhere else.
//
// This form only ever verifies a password. Nothing in this app — this page, the
// Profile screen, the mobile app, the Edge Functions — can create, change or
// reset one; the only way a password comes to exist is an operator running
// database/scripts/set-password.sql or the equivalent dashboard action. So the
// blast radius of the URL leaking is nil: every ordinary account has a null
// encrypted_password, and this form has no way to give it one.

import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { COMPANY } from "../lib/company";
import { authErrorMessage } from "../lib/authError";

export function SignInWithPassword() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    // On success the session arrives via onAuthStateChange and the route sends
    // us to /start, so there is nothing left to do here but report a failure.
    if (error) setError(authErrorMessage(error));
  }

  return (
    <div className="wrap auth-wrap">
      <h1>Sign in to {COMPANY.product}</h1>
      <p className="muted">
        Email and password sign-in, provided for app review and compliance accounts.
      </p>

      <form className="card" onSubmit={submit}>
        <label htmlFor="email">Email</label>
        <input id="email" type="email" required autoComplete="email" value={email}
               onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />

        <label htmlFor="password" style={{ marginTop: ".8rem" }}>Password</label>
        <input id="password" type="password" required autoComplete="current-password"
               value={password} onChange={(e) => setPassword(e.target.value)} />

        {error && <p className="err">{error}</p>}

        <button className="primary" style={{ marginTop: ".8rem" }} disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <p className="muted" style={{ marginBottom: 0 }}>
          By continuing you agree to our <Link to="/terms">Terms of Service</Link> and{" "}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </form>
    </div>
  );
}
